import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { APPLICATION_GLOBAL_SLUG } from "src/_config";
import InputTextField from "src/components/common/Form/InputTextField";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResetSuccess: () => void;
  token?: string; // Reset token from email link
}

interface ResetPasswordFormValues {
  otp: string;
  newPassword: string;
  confirmPassword: string;
}

const resetPasswordSchema = yup.object().shape({
  otp: yup
    .string()
    .matches(/^\d{6}$/, "OTP must be exactly 6 digits")
    .required("OTP is required"),
  newPassword: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .matches(/(?=.*[a-z])/, "Password must contain at least 1 lowercase letter")
    .matches(/(?=.*[A-Z])/, "Password must contain at least 1 uppercase letter")
    .matches(/(?=.*[0-9])/, "Password must contain at least 1 number")
    .matches(/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~])/, "Password must contain at least 1 special character")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose, onResetSuccess, token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Hooks (same as resetpassword.tsx)
  const userApi = useUserApi();
  const toast = useToaster();
  // const history = useHistory();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResetPasswordFormValues>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      otp: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const onSubmit = (data: ResetPasswordFormValues) => {
    setIsLoading(true);

    // Prepare API data (same structure as resetpassword.tsx)
    const parameters = {
      token: token || "", // Use provided token or empty string
      otp: data.otp,
      newPassword: data.newPassword,
      game_slug: APPLICATION_GLOBAL_SLUG,
    };

    userApi.callResetPassword(
      parameters,
      (message: string, resp: any) => {
        setIsLoading(false);
        if (resp) {
          toast.success(message);
          reset();
          // Auto close modal and trigger success callback
          setTimeout(() => {
            onResetSuccess();
          }, 1500);
        } else {
          toast.error(message);
        }
      },
      (message: string) => {
        setIsLoading(false);
        toast.error(message);
      },
    );
  };

  const handleClose = () => {
    reset();
    setIsLoading(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content signin-modal">
            <div className="modal-header border-0">
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-center">
              {/* Header with Logo */}
              <AuthModalHeader />

              {/* Lock Icon */}
              <div className="signin-icon mb-3">
                <div className="lock-icon">
                  <i className="fas fa-lock"></i>
                </div>
              </div>

              {/* Title */}
              <h2 className="signin-title mb-2">Create New Password</h2>
              <p className="signin-subtitle mb-4">Choose a strong password to secure your account</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* OTP Input */}
                <div className="password-input-container mb-4 position-relative">
                  <div className="input-group">
                    <span className="input-group-text email-modal-prefix">
                      <i className="fas fa-shield-alt"></i>
                    </span>
                    <div className="position-relative flex-grow-1">
                      <InputTextField
                        fieldName="otp"
                        fieldType="text"
                        control={control}
                        errors={errors}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>

                {/* New Password Input */}
                <div className="password-input-container mb-4 position-relative">
                  <div className="input-group">
                    <span className="input-group-text email-modal-prefix">
                      <i className="fas fa-key"></i>
                    </span>
                    <div className="position-relative flex-grow-1">
                      <InputTextField
                        fieldName="newPassword"
                        fieldType="password"
                        control={control}
                        errors={errors}
                        placeholder="Enter new password"
                        showPassword={showPassword}
                        toggleShowPassword={toggleShowPassword}
                        eyeStyle={{ top: "12px", right: "12px", zIndex: 10 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Confirm Password Input */}
                <div className="password-input-container mb-4 position-relative">
                  <div className="input-group">
                    <span className="input-group-text email-modal-prefix">
                      <i className="fas fa-key"></i>
                    </span>
                    <div className="position-relative flex-grow-1">
                      <InputTextField
                        fieldName="confirmPassword"
                        fieldType="password"
                        control={control}
                        errors={errors}
                        placeholder="Confirm new password"
                        showPassword={showConfirmPassword}
                        toggleShowPassword={toggleShowConfirmPassword}
                        eyeStyle={{ top: "12px", right: "12px", zIndex: 10 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="password-requirements mb-4">
                  <h6 className="requirements-title">Password Requirements:</h6>
                  <ul className="requirements-list">
                    <li className="requirement-item">At least 8 characters long</li>
                    <li className="requirement-item">One uppercase letter</li>
                    <li className="requirement-item">One lowercase letter</li>
                    <li className="requirement-item">One number</li>
                    <li className="requirement-item">One special character (!@#$%^&amp;* etc.)</li>
                  </ul>
                </div>

                {/* Reset Password Button */}
                <button type="submit" className="btn btn-success w-100 mb-3 email-modal-login-btn" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </form>

              {/* Security Notice */}
              <div className="security-notice mt-4">
                <p className="notice-text">
                  After resetting your password, you&apos;ll be automatically logged out from all devices for security.
                </p>
              </div>

              {/* Footer Links */}
              <AuthModalFooter />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default ResetPasswordModal;
